/**
 * AnalyticsTab - Week-over-week comparison and score distribution
 */
import { FC, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, Button } from '@pagespeedify/ui';
import { TrendingUp, TrendingDown, Minus, BarChart3 } from 'lucide-react';
import { MetricStats, FlattenedMonitoringHistory } from '../../../types';
import type { UserType } from '../../../lib/auth/types';

interface AnalyticsTabProps {
    history: FlattenedMonitoringHistory[];
    fieldDataHistory: any[]; // Field data from additional_metrics.field_data_history
    stats: Record<string, MetricStats>;
    monitoringUsage?: any;
    user?: { uid: string; email: string | null; user_type?: UserType } | null;
}

const METRIC_CONFIG = {
    performance_score: { label: 'Score', unit: '', divisor: 1, inverse: true },
    lcp: { label: 'LCP', unit: 's', divisor: 1000, inverse: false },
    cls: { label: 'CLS', unit: '', divisor: 1, inverse: false },
    tbt: { label: 'TBT', unit: 'ms', divisor: 1, inverse: false },
};

const THRESHOLDS = {
    performance_score: { good: 90, poor: 50 },
    lcp: { good: 2500, poor: 4000 },
    cls: { good: 0.1, poor: 0.25 },
    tbt: { good: 200, poor: 600 },
};

function getTrendArrow(trend: number | null, inverse: boolean): { icon: typeof TrendingUp; color: string; text: string } {
    if (trend === null) return { icon: Minus, color: 'text-muted-foreground', text: '-' };
    const isGood = inverse ? trend > 0 : trend < 0;
    const isBad = inverse ? trend < 0 : trend > 0;
    const text = `${trend > 0 ? '+' : ''}${trend.toFixed(1)}%`;

    if (isGood) return { icon: TrendingUp, color: 'text-green-600 dark:text-green-400', text };
    if (isBad) return { icon: TrendingDown, color: 'text-red-600 dark:text-red-400', text };
    return { icon: Minus, color: 'text-muted-foreground', text };
}

function getDistribution(history: FlattenedMonitoringHistory[], metric: string) {
    const threshold = THRESHOLDS[metric as keyof typeof THRESHOLDS];
    if (!threshold || history.length === 0) return { good: 0, needsImprovement: 0, poor: 0 };

    let good = 0, needsImprovement = 0, poor = 0;
    history.forEach(h => {
        const rawValue = (h as any)[metric];
        if (rawValue === null || rawValue === undefined) return;
        const value = Number(rawValue);
        if (Number.isNaN(value)) return;

        if (metric === 'performance_score') {
            if (value >= threshold.good) good++;
            else if (value <= threshold.poor) poor++;
            else needsImprovement++;
        } else {
            if (value <= threshold.good) good++;
            else if (value >= threshold.poor) poor++;
            else needsImprovement++;
        }
    });

    const total = good + needsImprovement + poor;
    return {
        good: total > 0 ? (good / total) * 100 : 0,
        needsImprovement: total > 0 ? (needsImprovement / total) * 100 : 0,
        poor: total > 0 ? (poor / total) * 100 : 0,
    };
}

export const AnalyticsTab: FC<AnalyticsTabProps> = ({ history, fieldDataHistory, stats }) => {
    const [period, setPeriod] = useState('7');

    // Filter history based on period
    const filteredHistory = useMemo(() => {
        if (period === 'all') return history;
        if (period === 'year') {
            const startOfYear = new Date(new Date().getFullYear(), 0, 1);
            return history.filter(h => new Date(h.scan_date) >= startOfYear);
        }
        const days = parseInt(period);
        const cutoff = new Date(Date.now() - days * 24 * 60 * 60 * 1000);
        return history.filter(h => new Date(h.scan_date) >= cutoff);
    }, [history, period]);

    // Calculate field data averages from CrUX data
    const fieldDataStats = useMemo(() => {
        if (!fieldDataHistory || fieldDataHistory.length === 0) return null;

        const latestFieldScan = fieldDataHistory[0];
        const crux = latestFieldScan?.crux || {};

        return {
            lcp: crux['LARGEST_CONTENTFUL_PAINT_MS']?.percentile || null,
            cls: crux['CUMULATIVE_LAYOUT_SHIFT_SCORE']?.percentile || null,
            fcp: crux['FIRST_CONTENTFUL_PAINT_MS']?.percentile || null,
            ttfb: crux['EXPERIMENTAL_TIME_TO_FIRST_BYTE']?.percentile || null,
        };
    }, [fieldDataHistory]);

    const isEmpty = filteredHistory.length === 0;
    const hasEnoughData = filteredHistory.length >= 2;
    const hasFieldData = fieldDataStats !== null;

    return (
        <div className="space-y-6">
            {/* Period Selector - button toggle like History tab */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <BarChart3 className="h-5 w-5 text-muted-foreground" />
                    <span className="font-medium">Analytics</span>
                </div>
                <div className="flex gap-1 p-1 bg-muted/50 rounded-lg">
                    {[
                        { value: '7', label: '7 days' },
                        { value: '30', label: '30 days' },
                        { value: '90', label: '3 months' },
                        { value: 'year', label: 'This year' },
                        { value: 'all', label: 'All' },
                    ].map(opt => (
                        <Button
                            key={opt.value}
                            variant={period === opt.value ? 'default' : 'ghost'}
                            size="sm"
                            onClick={() => setPeriod(opt.value)}
                            className={`text-xs px-2 h-7 ${period === opt.value ? '' : 'text-muted-foreground hover:text-foreground'}`}
                        >
                            {opt.label}
                        </Button>
                    ))}
                </div>
            </div>

            {/* Empty State */}
            {isEmpty && (
                <Card>
                    <CardContent className="py-12 text-center text-muted-foreground">
                        <BarChart3 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                        <p>No analytics data for the selected period.</p>
                    </CardContent>
                </Card>
            )}

            {!isEmpty && (
                <>
                    {/* Period-over-Period Comparison Table */}
                    <Card>
                        <CardHeader className="pb-3">
                            <CardTitle className="text-base">Period-over-Period Comparison</CardTitle>
                        </CardHeader>
                        <CardContent>
                            {!hasEnoughData ? (
                                <p className="text-sm text-muted-foreground">Need at least 2 scans to show comparison.</p>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="border-b">
                                                <th className="text-left py-2 font-medium">Metric</th>
                                                <th className="text-right py-2 font-medium">Lab Avg</th>
                                                {hasFieldData && <th className="text-right py-2 font-medium">Real Users</th>}
                                                <th className="text-right py-2 font-medium">Trend</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {Object.entries(METRIC_CONFIG).map(([metric, config]) => {
                                                const stat = stats[metric];
                                                if (!stat || stat.dataPoints === 0) return null;
                                                const { icon: Icon, color, text } = getTrendArrow(stat.trend, config.inverse);
                                                const avgFormatted = (stat.avg / config.divisor).toFixed(metric === 'cls' ? 3 : metric === 'performance_score' ? 0 : 2);

                                                // Get field data value if available
                                                const fieldValue = fieldDataStats?.[metric as keyof typeof fieldDataStats];
                                                const fieldFormatted = fieldValue !== null && fieldValue !== undefined
                                                    ? (fieldValue / config.divisor).toFixed(metric === 'cls' ? 3 : 2)
                                                    : '-';

                                                return (
                                                    <tr key={metric} className="border-b last:border-b-0">
                                                        <td className="py-3">{config.label}</td>
                                                        <td className="text-right py-3 font-medium">
                                                            {avgFormatted}{config.unit}
                                                        </td>
                                                        {hasFieldData && (
                                                            <td className="text-right py-3">
                                                                <span className="font-medium text-blue-600 dark:text-blue-400">
                                                                    {fieldFormatted !== '-' ? `${fieldFormatted}${config.unit}` : '-'}
                                                                </span>
                                                            </td>
                                                        )}
                                                        <td className="text-right py-3">
                                                            <span className={`flex items-center justify-end gap-1 ${color}`}>
                                                                <Icon className="h-4 w-4" />
                                                                {text}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Score Distribution */}
                    <Card>
                        <CardHeader className="pb-3">
                            <CardTitle className="text-base">Core Web Vitals Distribution</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-4">
                                {['lcp', 'cls', 'tbt'].map(metric => {
                                    const dist = getDistribution(filteredHistory, metric);
                                    const label = METRIC_CONFIG[metric as keyof typeof METRIC_CONFIG]?.label || metric.toUpperCase();

                                    return (
                                        <div key={metric}>
                                            <div className="flex items-center justify-between mb-1">
                                                <span className="text-sm font-medium">{label}</span>
                                                <span className="text-xs text-muted-foreground">
                                                    {dist.good.toFixed(0)}% Good
                                                </span>
                                            </div>
                                            <div className="h-2 rounded-full bg-muted overflow-hidden flex">
                                                <div
                                                    className="bg-green-500 h-full"
                                                    style={{ width: `${dist.good}%` }}
                                                />
                                                <div
                                                    className="bg-amber-500 h-full"
                                                    style={{ width: `${dist.needsImprovement}%` }}
                                                />
                                                <div
                                                    className="bg-red-500 h-full"
                                                    style={{ width: `${dist.poor}%` }}
                                                />
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="flex items-center justify-center gap-4 mt-4 text-xs">
                                <span className="flex items-center gap-1">
                                    <span className="w-2 h-2 rounded-full bg-green-500" />
                                    Good
                                </span>
                                <span className="flex items-center gap-1">
                                    <span className="w-2 h-2 rounded-full bg-amber-500" />
                                    Needs Improvement
                                </span>
                                <span className="flex items-center gap-1">
                                    <span className="w-2 h-2 rounded-full bg-red-500" />
                                    Poor
                                </span>
                            </div>
                        </CardContent>
                    </Card>
                </>
            )}
        </div>
    );
};

export default AnalyticsTab;
